/**
 * @file    useTableFetch
 * @scope   SHARED
 * @summary Owns the widget-data request: builds the payload from the widget context and fetches.
 * @consumes WidgetContext, global store endpoints, useAxios, payloadBuilder
 * @produces { response, getData, controllerNameValue }
 * @sideEffects Network request through getDataFromSingleEndpoint (only when not first render).
 */
import { MutableRefObject, useState } from 'react';
import { useWidgetContext } from 'contexts/WidgetProvider';
import { useAxios } from 'hooks/useAxios';
import { getDataFromSingleEndpoint } from 'services/Torogoz/TogorozApi';
import { useStore as useGlobalStore } from 'store/useGlobalStore';
import { buildFetchPayload, resolveControllerName } from '../utils/payloadBuilder';

interface UseTableFetchArgs {
  payload: any;
  /** Pagination or server sorting: adds page/sort fields to the request. */
  paginated: boolean;
  /** Per-page size used when the caller does not pass one. */
  defaultPerPage: number;
  /** WHY: requests are skipped until the first user/context interaction (shared guard). */
  firstRender: MutableRefObject<boolean>;
}

export const useTableFetch = ({
  payload,
  paginated,
  defaultPerPage,
  firstRender,
}: UseTableFetchArgs) => {
  const endpoints = useGlobalStore((state: any) => state.endpoints);
  const { chartView, selectedValueL1, selectedValueL2, searchFilter } = useWidgetContext();
  const { response, axiosFetch } = useAxios();
  const [controllerNameValue, setControllerNameValue] = useState<string>('');

  const getData = async (
    page: number,
    perPage: number = defaultPerPage,
    selectedColumn: any = null,
    direction = 'asc'
  ) => {
    if (firstRender.current) return;
    const resolved = resolveControllerName(payload, chartView);
    setControllerNameValue(resolved.controllerNameValue);

    const newPayload = buildFetchPayload({
      payload,
      controllerName: resolved.controllerName,
      chartView,
      searchFilter,
      selectedValueL1,
      selectedValueL2,
      paginated,
      firstRender: firstRender.current,
      page,
      perPage,
      selectedColumn,
      direction,
    });
    await getDataFromSingleEndpoint(endpoints.getWidgetData, newPayload, axiosFetch);
  };

  return { response, getData, controllerNameValue };
};
